import { TRPCClientError, type TRPCLink } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { observable } from '@trpc/server/observable';

export const GENERIC_TRPC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Shown when the request never reached the server at all.
 *
 * Distinguished from GENERIC_TRPC_ERROR_MESSAGE on purpose: sanitizing is
 * fail-closed on an unrecognized error shape, and a transport failure carries
 * no `data`, so it used to read as a server fault the user can only retry
 * blindly. Being offline is the one failure the user can actually fix.
 */
export const NETWORK_TRPC_ERROR_MESSAGE =
  'Cannot reach the server. Check your connection and try again.';

const MISSING_PROCEDURE_MESSAGE = /^No procedure found on path(?:\s|$)/i;

function getHttpStatus(error: TRPCClientError<AnyRouter>): number | undefined {
  const data: unknown = error.data;
  if (!data || typeof data !== 'object' || !('httpStatus' in data)) return undefined;
  return typeof data.httpStatus === 'number' ? data.httpStatus : undefined;
}

/**
 * A fetch that never completed. The browser rejects with a TypeError, so the
 * error reaches us with no tRPC `data` and no shape — indistinguishable from an
 * unrecognized server error by status alone.
 */
function isTransportFailure(
  error: TRPCClientError<AnyRouter>,
): error is TRPCClientError<AnyRouter> & { cause: TypeError } {
  return error.data === undefined && error.shape === undefined && error.cause instanceof TypeError;
}

function needsSanitizing(error: TRPCClientError<AnyRouter>): boolean {
  const httpStatus = getHttpStatus(error);
  return (
    httpStatus === undefined || httpStatus >= 500 || MISSING_PROCEDURE_MESSAGE.test(error.message)
  );
}

export function sanitizeTRPCClientError(
  error: TRPCClientError<AnyRouter>,
): TRPCClientError<AnyRouter> {
  if (isTransportFailure(error)) {
    return new TRPCClientError(NETWORK_TRPC_ERROR_MESSAGE, { cause: error.cause });
  }

  if (!needsSanitizing(error)) return error;

  const result = error.shape
    ? {
        error: {
          ...error.shape,
          message: GENERIC_TRPC_ERROR_MESSAGE,
        },
      }
    : undefined;

  return new TRPCClientError(GENERIC_TRPC_ERROR_MESSAGE, {
    ...(result ? { result } : {}),
  });
}

export const trpcErrorSanitizerLink: TRPCLink<AnyRouter> = () => {
  return ({ next, op }) =>
    observable((observer) => {
      const subscription = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(error) {
          observer.error(sanitizeTRPCClientError(error));
        },
        complete() {
          observer.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
};
